import { useEffect, useState, useCallback, useMemo } from "react";
import { apiHandler } from "../../../api";
import { endpoint } from "../../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";


const mapPendingResolutions = (pendingResolutions) => {
    // If there is only one resolution object AND it lacks a 'parent_details' and 'options' array, treat it as the single, old format.
    // Otherwise, assume the new item-grouped format.
    if (pendingResolutions.length === 1 && !pendingResolutions[0].parent_details && !pendingResolutions[0].options) {
        const singleRes = pendingResolutions[0];
        const resId = singleRes.id;
        const resCode = singleRes.description?.code || "RESOLUTION";

        // Construct a pseudo-group for a single resolution to simplify rendering logic
        return [{
            id: resId, // Use the resolution ID as the group ID
            parent_details: { // Fake parent details for consistency
                id: resId,
                short_desc: "Single Resolution Proposed",
                proposed_by: singleRes.proposed_by,
                proposed_at: singleRes.proposed_at,
            },
            options: [{
                id: resId,
                description: singleRes.description,
            }],
            resolution_history: singleRes.resolution_history || [],
           
        }];
    }
    
    // For the new multi-item, multi-option structure, return as is (or map if needed)
    return pendingResolutions;
};

export const useReportedIssueDetails = ({ issueId, onBack }) => {
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);

  const [issueData, setIssueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeDetailsTab, setActiveDetailsTab] = useState("info");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

const [isResolutionSubmitting, setIsResolutionSubmitting] = useState(false); // New state

    // --- NEW RESOLUTION LOGIC STATES ---
    const [selectedResolutions, setSelectedResolutions] = useState({});

  const [formData, setFormData] = useState({
    short_desc: "",
    long_desc: "",
    images: [],
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setFormData({ short_desc: "", long_desc: "", images: [] });
  };

  const pendingResolutionGroups = useMemo(() => {
        const resolutions = issueData?.PendingResolutions || [];
        // Map resolutions into the new unified group structure
        return mapPendingResolutions(resolutions);
    }, [issueData?.PendingResolutions]);

    // Use effect to reset selections when resolutions change (e.g., on refetch)
    useEffect(() => {
        if (pendingResolutionGroups.length > 0) {
            // Re-initialize selections based on the number of groups
            const initialSelections = {};
            pendingResolutionGroups.forEach(group => {
                // If the group has a resolution_history with a final status, pre-select it or mark as completed
                const finalResolution = group.resolution_history?.[0];
                if (finalResolution?.status) {
                     // Check if it was accepted or rejected
                    const isAccepted = finalResolution.status === "RESOLUTION_ACCEPTED";
                    initialSelections[group.id] = {
                        selected_resolution_id: isAccepted ? finalResolution.description.id : group.id, // Use option ID if accepted, group ID if rejected
                        accepted: isAccepted ? "true" : "false"
                    };
                }
            });
            setSelectedResolutions(initialSelections);
        } else {
            setSelectedResolutions({});
        }
    }, [pendingResolutionGroups]);

  // Fetch issue details
  const getIssueDetails = useCallback(async () => {
    if (!issueId) return;
    try {
      setLoading(true);
      setError(null);

      const result = await apiHandler({
        url: `${endpoint.GET_ISSUE_DETAILS}?id=${issueId}&issue_type=REPORTED`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      setIssueData(result?.data || null);
    } catch (err) {
      console.error("Error fetching issue details:", err);
      setError(err.message || "Failed to fetch issue details");
      setIssueData(null);
    } finally {
      setLoading(false);
    }
  }, [issueId, authData, dispatch, storeId]);

  useEffect(() => {
    getIssueDetails();
  }, [getIssueDetails]);

  // Back navigation
  const handleBack = () => {
    if (onBack) onBack();
    else window.history.back();
  };

const handleResolutionSelection = useCallback((parentId, resolutionId, accepted) => {
        setSelectedResolutions(prev => ({
            ...prev,
            [parentId]: {
                selected_resolution_id: resolutionId,
                accepted: accepted,
            }
        }));
    }, []);

    const isResolutionGroupValid = useCallback(() => {
        // Check if a selection has been made for EVERY group/item
        return pendingResolutionGroups.length > 0 && 
               pendingResolutionGroups.every(group => !!selectedResolutions[group.id]);
    }, [pendingResolutionGroups, selectedResolutions]);

    const handleResolutionSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!isResolutionGroupValid()) {
            addToast("Please select an action for every item before submitting.", { appearance: "warning" });
            return;
        }

        const payload = {
            response: Object.values(selectedResolutions).map(selection => ({
                selected_resolution_id: selection.selected_resolution_id,
                accepted: selection.accepted,
            }))
        };
        
        try {
            setIsResolutionSubmitting(true);

            const response = await apiHandler({
                url: `${endpoint.RESPOND_TO_RESOLUTION}/${issueId}`,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                authToken: authData,
                dispatch,
                storeId,
                data: payload,
            });

            if (response?.data?.result?.success) {
                addToast(response.data.result.message || "All resolutions submitted!", {
                    appearance: "success",
                });
                getIssueDetails(); // refresh
            } else {
                throw new Error(response?.data?.result?.message || "Failed to submit resolutions");
            }
        } catch (err) {
            console.error("Error submitting resolutions:", err);
            addToast(err.message || "Error submitting resolutions", {
                appearance: "error",
            });
        } finally {
            setIsResolutionSubmitting(false);
        }
    }, [issueId, selectedResolutions, isResolutionGroupValid, authData, dispatch, storeId, addToast, getIssueDetails, pendingResolutionGroups]);

  const handleProvideInfoClick = (req) => {
    if (!req) return;
    setSelectedRequest(req);
    setFormData({
      short_desc: "",
      long_desc: "",
      images: [], // or prefill if available in req
    });
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!selectedRequest) return;

    const payload = {
      short_desc: formData.short_desc,
      detailed_desc: formData.long_desc,
      images: formData.images,
      ref_id: selectedRequest.id,
    };

    try {
      setLoading(true);
      const response = await apiHandler({
        url: `${endpoint.PROVIDE_INFO}/${issueId}`, // dynamic issueId
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      const result = response?.data?.result;

      if (result?.success) {
        addToast(result.message || "Info provided successfully", {
          appearance: "success",
          autoDismiss: true,
        });
        getIssueDetails(); // refresh issue data
        closeModal();
      } else {
        addToast(result?.message || "Failed to provide info", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error("Error providing info:", err);
      addToast("Something went wrong. Please try again.", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async (message) => {
    if (!issueId) return;

    try {
      setLoading(true);

      const payload = {
        short_desc:
          message ||
          formData.short_desc ||
          "Issue is being escalated as resolution wasn't satisfying",
      };

      const response = await apiHandler({
        url: `${endpoint.ESCALATE_ISSUE}/${issueId}`,
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      const result = response?.data?.result;

      if (result?.success) {
        addToast(result.message || "Issue escalated successfully", {
          appearance: "success",
          autoDismiss: true,
        });

        // ✅ Instantly update local issue status so button disables right away
        setIssueData((prev) => ({
          ...prev,
          status: "ESCALATED",
        }));

        // ✅ Optionally refresh from server for latest info
        getIssueDetails();
      } else {
        throw new Error(result?.message || "Failed to escalate issue");
      }
    } catch (err) {
      console.error("Error escalating issue:", err);
      addToast(err.message || "Error while escalating issue", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseIssue = async (message) => {
    if (!issueId) return;

    try {
      setLoading(true);

      // Prepare payload
      const payload = {
        short_desc:
          message ||
          formData.short_desc ||
          "Closing issue as it has been resolved",
      };

      // API call
      const response = await apiHandler({
        url: `${endpoint.CLOSE_ISSUE}/${issueId}`,
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      const result = response?.data?.result;

      if (result?.success) {
        addToast(result.message || "Issue closed successfully", {
          appearance: "success",
          autoDismiss: true,
        });

        setIssueData((prev) => ({
          ...prev,
          status: "CLOSED",
        }));

        getIssueDetails();
      } else {
        throw new Error(result?.message || "Failed to close issue");
      }
    } catch (err) {
      console.error("Error closing issue:", err);
      addToast(err.message || "Error while closing issue", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusRequest = async () => {
    if (!issueId) return;

    const issueDetails = issueData?.issue_details;
    if (!issueDetails) {
      addToast("Issue data is not loaded yet.", { appearance: "error" });
      return;
    }

    try {
      setLoading(true);

      const refs =
        issueDetails.category?.map((category) => ({
          ref_type: category.type,
          ref_id: category.id,
        })) || []; // Default to empty array if 'category' is missing

      const payload = {
        issue_category: issueDetails.category?.[0]?.type,

        issue_subcategory: issueDetails.issue_description?.short_desc,

        short_desc: issueDetails.issue_description?.long_desc,

        bap_id: issueDetails.complainant_details?.org_name,

        image_url: issueDetails.issue_description?.images?.[0],

        refs: refs,
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await apiHandler({
        url: `${endpoint.REQUEST_STATUS}/${issueId}`,
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      const result = response?.data?.result;

      if (result?.success) {
        addToast(result.message || "Status request sent successfully!", {
          appearance: "success",
          autoDismiss: true,
        });
        getIssueDetails(); // Refresh data to show new action in timeline
      } else {
        throw new Error(result?.message || "Failed to send status request");
      }
    } catch (err) {
      console.error("Error sending status request:", err);
      addToast(err.message || "Error while sending status request", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      // e.target.files is a FileList, convert to array
      setFormData((prev) => ({
        ...prev,
        images: Array.from(e.target.files),
      }));
    }
  };

  return {
    issueData,
        loading,
        error,
        refetch: getIssueDetails,
        activeDetailsTab,
        setActiveDetailsTab,
        handleBack,
        handleProvideInfoClick,
        handleSave,
        isModalOpen,
        closeModal,
        formData,
        setFormData,
        selectedRequest,
        setSelectedRequest,
        handleEscalate,
        handleCloseIssue,
        handleStatusRequest,
        handleFileChange,
        // New exports:
        pendingResolutionGroups,
        selectedResolutions,
        handleResolutionSelection,
        handleResolutionSubmit,
        isResolutionGroupValid,
        isResolutionSubmitting,
  };
};
